/**
 * TanStack Query hooks for "Campanhas FF" (ADM-32 / BACK-158).
 *
 * Coupons go through the generated, typed `api` client (the endpoint is
 * published; only `budgetCents`/`spentCents` are missing from the generated
 * schema, so responses are cast to the locally-extended `CouponResponse` —
 * same spirit as `features/banners/hooks.ts`'s reorder `as never` cast, just
 * narrower). Promotions aren't in the generated surface at all, so they go
 * through the small typed fetch helper already built for the Finance feature
 * (`features/finance/api.ts` — plain `/api/v1/admin{path}` GET/POST, nothing
 * finance-specific about it).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import { adminGet, adminPost } from '../finance/api';
import type {
  CouponResponse,
  CreateCouponRequest,
  CreateFreeShippingPromotionRequest,
  PageResponse,
  PromotionResponse,
} from './types';

export const campaignsKeys = {
  coupons: ['campaigns', 'coupons'] as const,
  freeShipping: (page: number, size: number) =>
    ['campaigns', 'free-shipping-promotions', page, size] as const,
};

// ---- coupons (BACK-158 budget) ----------------------------------------------

export function useCouponsList(): UseQueryResult<CouponResponse[], Error> {
  return useQuery({
    queryKey: campaignsKeys.coupons,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/coupons');
      if (error) throw error as Error;
      return (data ?? []) as CouponResponse[];
    },
  });
}

export function useCreateCoupon(): UseMutationResult<CouponResponse, Error, CreateCouponRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data, error } = await api.POST('/api/v1/admin/coupons', { body });
      if (error) throw error as Error;
      return data as CouponResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignsKeys.coupons });
    },
  });
}

// ---- FREE_SHIPPING promotions sponsored by the platform ---------------------

export function useFreeShippingPromotions(
  page: number,
  size: number,
): UseQueryResult<PageResponse<PromotionResponse>, Error> {
  return useQuery({
    queryKey: campaignsKeys.freeShipping(page, size),
    queryFn: () =>
      adminGet<PageResponse<PromotionResponse>>('/promotions', {
        type: 'FREE_SHIPPING',
        page,
        size,
      }),
  });
}

export function useCreateFreeShippingPromotion(): UseMutationResult<
  PromotionResponse,
  Error,
  CreateFreeShippingPromotionRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      adminPost<PromotionResponse, CreateFreeShippingPromotionRequest>('/promotions', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns', 'free-shipping-promotions'] });
    },
  });
}
